import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calculator, Calendar, FileText, Download, Settings, Zap, DollarSign, GitBranch } from "lucide-react";


import PayrollPeriods from "./PayrollPeriods";
import PayrollCalculations from "./PayrollCalculations";
import PayrollReports from "./PayrollReports";
import PayrollExports from "./PayrollExports";
import PayrollSettings from "./PayrollSettings";
import PayrollClosingAgent from "./PayrollClosingAgent";

const PayrollHub = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "periods";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const tabs = [
    {
      id: "periods",
      label: t("payrollHub.tabs.periods"),
      icon: Calendar,
      description: t("payrollHub.tabs.periodsDesc"),
      section: t("payrollHub.sections.management"),
      sectionKey: "management",
      step: 1,
    },
    {
      id: "calculations",
      label: t("payrollHub.tabs.calculations"),
      icon: Calculator,
      description: t("payrollHub.tabs.calculationsDesc"),
      section: t("payrollHub.sections.management"),
      sectionKey: "management",
      step: 1,
    },
    {
      id: "reports",
      label: t("payrollHub.tabs.reports"),
      icon: FileText,
      description: t("payrollHub.tabs.reportsDesc"),
      section: t("payrollHub.sections.consultation"),
      sectionKey: "consultation",
      step: 2,
    },
    {
      id: "exports",
      label: t("payrollHub.tabs.exports"),
      icon: Download,
      description: t("payrollHub.tabs.exportsDesc"),
      section: t("payrollHub.sections.consultation"),
      sectionKey: "consultation",
      step: 2,
    },
    {
      id: "closing",
      label: t("payrollHub.tabs.closing"),
      icon: Zap,
      description: t("payrollHub.tabs.closingDesc"),
      section: t("payrollHub.sections.aiAgents"),
      sectionKey: "aiAgents",
      step: 3,
    },
    {
      id: "settings",
      label: t("payrollHub.tabs.settings"),
      icon: Settings,
      description: t("payrollHub.tabs.settingsDesc"),
      section: t("payrollHub.sections.administration"),
      sectionKey: "administration",
      step: 4,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "periods":      return <PayrollPeriods />;
      case "calculations": return <PayrollCalculations />;
      case "reports":      return <PayrollReports />;
      case "exports":      return <PayrollExports />;
      case "closing":      return <PayrollClosingAgent />;
      case "settings":     return <PayrollSettings />;
      default:             return <PayrollPeriods />;
    }
  };

  const currentTab = tabs.find((t) => t.id === activeTab);

  // Unified DS colors — all sections use the same lavender accent
  const dsActive = { bg: "var(--lm-s2)", border: "#5e6ad2", text: "#5e6ad2" };
  const dsInactive = { bg: "var(--lm-s1)", border: "var(--lm-border)", text: "var(--lm-ink-2)" };

  const sections = [
    { sectionKey: "management",    step: 1, tabs: tabs.filter((t) => t.sectionKey === "management") },
    { sectionKey: "consultation",  step: 2, tabs: tabs.filter((t) => t.sectionKey === "consultation") },
    { sectionKey: "aiAgents",      step: 3, tabs: tabs.filter((t) => t.sectionKey === "aiAgents") },
    { sectionKey: "administration",step: 4, tabs: tabs.filter((t) => t.sectionKey === "administration") },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 sm:text-3xl" style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}>{t("payrollHub.title")}</h2>
          <p className="mt-1 text-sm" style={{ color: "var(--lm-ink-2)" }}>{t("payrollHub.subtitle")}</p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
          style={{ color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.10)", border: "1px solid rgba(94,106,210,0.20)" }}
        >
          <DollarSign className="h-3.5 w-3.5" />
          {t("payrollHub.module")}
        </span>
      </div>

      {/* Main Content Card */}
      <div
        className="rounded-xl"
        style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
      >
        <div className="p-8">
          {/* Navigation Title */}
          <div className="mb-6">
            <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "var(--lm-ink)" }}>
              <GitBranch className="h-5 w-5" style={{ color: "var(--lm-ink-2)" }} />
              {t("payrollHub.quickNav")}
            </h3>
            <p className="text-sm mt-1" style={{ color: "var(--lm-ink-2)" }}>{t("payrollHub.quickNavDesc")}</p>
          </div>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {sections.map((section) => {
              const sectionTabs = section.tabs;
              const hasActiveTab = sectionTabs.some((t) => t.id === activeTab);

              return (
                <div
                  key={section.sectionKey}
                  className="rounded-xl transition-all"
                  style={{
                    backgroundColor: hasActiveTab ? "var(--lm-s2)" : "var(--lm-s1)",
                    border: hasActiveTab ? `1px solid ${dsActive.border}` : `1px solid ${dsInactive.border}`,
                    borderLeft: hasActiveTab ? `3px solid ${dsActive.border}` : undefined,
                  }}
                >
                  <div className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>
                        {t(`payrollHub.sections.${section.sectionKey}`)}
                      </h4>
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={hasActiveTab
                          ? { color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.12)", border: "1px solid rgba(94,106,210,0.25)" }
                          : { color: "var(--lm-ink-3)", backgroundColor: "var(--lm-s3)", border: "1px solid var(--lm-border)" }
                        }
                      >
                        {t("payrollHub.step", { step: section.step })}
                      </span>
                    </div>

                    <div style={{ height: "1px", backgroundColor: "var(--lm-border)" }} />

                    <div className="space-y-1.5">
                      {sectionTabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;

                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left text-sm font-medium"
                            style={{
                              backgroundColor: isActive ? "#5e6ad2" : "transparent",
                              color: isActive ? "#ffffff" : "var(--lm-ink-2)",
                              border: isActive ? "1px solid #5e6ad2" : "1px solid transparent",
                            }}
                            onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "rgba(94,106,210,0.08)"; e.currentTarget.style.color = "var(--lm-ink)"; } }}
                            onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "var(--lm-ink-2)"; } }}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="flex-1">{tab.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ height: "1px", backgroundColor: "var(--lm-border)", margin: "32px 0" }} />

          {/* Current Section Context Banner */}
          {currentTab && (
            <div
              className="rounded-xl mb-6"
              style={{ backgroundColor: "var(--lm-s2)", border: "1px solid rgba(94,106,210,0.25)", borderLeft: "3px solid #5e6ad2" }}
            >
              <div className="py-4 px-5 flex items-center gap-4">
                <div
                  className="p-3 rounded-xl flex-shrink-0"
                  style={{ backgroundColor: "#5e6ad2" }}
                >
                  {(() => { const Icon = currentTab.icon; return <Icon className="h-5 w-5 text-white" />; })()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base font-semibold" style={{ color: "var(--lm-ink)" }}>{currentTab.label}</span>
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.12)", border: "1px solid rgba(94,106,210,0.25)" }}
                    >
                      {t("payrollHub.step", { step: currentTab.step })}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{currentTab.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="min-h-[500px]">{renderTabContent()}</div>
        </div>
      </div>

      {/* Info Footer */}
      <div
        className="rounded-xl"
        style={{ backgroundColor: "var(--lm-s1)", border: "1px solid rgba(94,106,210,0.15)" }}
      >
        <div className="p-6 flex items-start gap-4">
          <div
            className="p-3 rounded-xl flex-shrink-0"
            style={{ background: "linear-gradient(135deg, rgba(94,106,210,0.15) 0%, rgba(94,106,210,0.05) 100%)", border: "1px solid rgba(94,106,210,0.15)" }}
          >
            <DollarSign className="h-6 w-6" style={{ color: "#5e6ad2" }} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-base mb-2" style={{ color: "var(--lm-ink)" }}>{t("payrollHub.systemTitle")}</h4>
            <p className="text-sm leading-relaxed" style={{ color: "var(--lm-ink-2)" }}>{t("payrollHub.systemDesc")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              {[
                { icon: Calculator, label: t("payrollHub.automaticCalc") },
                { icon: GitBranch, label: t("payrollHub.approvalWorkflow") },
                { icon: Download, label: t("payrollHub.accountingExports") },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 text-sm font-medium" style={{ color: "var(--lm-ink-2)" }}>
                  <div
                    className="p-1.5 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: "rgba(94,106,210,0.10)", border: "1px solid rgba(94,106,210,0.15)" }}
                  >
                    <Icon className="h-4 w-4" style={{ color: "#5e6ad2" }} />
                  </div>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollHub;
